import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { authFetch } from "../../App";
import {
  ArrowLeft, Power, ChevronUp, ChevronDown, Maximize2, X,
} from "lucide-react";
import { SchaltplanSVG } from "./SchaltplanSVG";

const API = "/api";
const KLEMMPLAN_IMG = "https://static.prod-images.emergentagent.com/jobs/595001d4-da5a-40c1-993c-94169d35bde0/images/c1813b7d2bdcec7db320f959fae17847812e2d56b5ae2a06a51bc4ccd592edb0.png";

export default function TorampelSteuerung() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [showSchaltplanFullscreen, setShowSchaltplanFullscreen] = useState(false);
  const [showKlemmplanFullscreen, setShowKlemmplanFullscreen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  // Simulation State
  const [hauptschalter, setHauptschalter] = useState(false);
  const [wahlschalter, setWahlschalter] = useState(0);
  const [torOffen, setTorOffen] = useState(true);
  const [torAnimating, setTorAnimating] = useState(false);

  useEffect(() => {
    authFetch(`${API}/projects/${projectId}`).then(async (r) => {
      if (r.ok) setProject(await r.json());
    });
  }, [projectId]);

  // Endschalter logic (Eaton LS-11S/S Wechslerkontakt)
  // Endschalter betaetigt = Tor OFFEN → NO schliesst (ROT AN), NC oeffnet (GRUEN unterbrochen)
  // Endschalter NICHT betaetigt = Tor GESCHLOSSEN → Wahlschalter uebernimmt
  const endschalterBetaetigt = torOffen; // betaetigt wenn Tor OFFEN
  let ampelRot = false;
  let ampelGruen = false;

  if (hauptschalter) {
    if (endschalterBetaetigt) {
      // PRIORITAET: Tor OFFEN → immer ROT, GRUEN gesperrt
      ampelRot = true;
      ampelGruen = false;
    } else {
      // Tor GESCHLOSSEN → Wahlschalter steuert
      ampelRot = wahlschalter === 1;
      ampelGruen = wahlschalter === 2;
    }
  }

  const animateGate = useCallback((targetOpen) => {
    if (torAnimating) return;
    setTorAnimating(true);
    setTimeout(() => {
      setTorOffen(targetOpen);
      setTorAnimating(false);
    }, 800);
  }, [torAnimating]);

  useEffect(() => {
    if (!hauptschalter) setWahlschalter(0);
  }, [hauptschalter]);

  const projectName = project?.display_name || project?.name || "Projekt";

  return (
    <div className="min-h-screen bg-[#0f1117] text-white" data-testid="torampel-page">
      {/* ── Compact Header ── */}
      <div className="sticky top-0 z-30 px-3 py-2 border-b border-white/5" style={{ background: "rgba(15,17,23,0.95)", backdropFilter: "blur(12px)" }}>
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-[11px] font-medium text-gray-400 active:text-white" data-testid="torampel-back-btn">
            <ArrowLeft className="w-3.5 h-3.5" />
            Zurueck
          </button>
          <div className="flex items-center gap-1.5">
            <div className={`w-1.5 h-1.5 rounded-full ${hauptschalter ? "bg-emerald-400 animate-pulse" : "bg-gray-600"}`} />
            <span className={`text-[9px] font-semibold ${hauptschalter ? "text-emerald-400" : "text-gray-500"}`}>
              {hauptschalter ? "System aktiv" : "System aus"}
            </span>
          </div>
        </div>
        <p className="text-[11px] font-bold text-white mt-0.5 leading-none">Industrieschaltung Simulator</p>
        <p className="text-[9px] text-gray-500">WERMA LED-Ampel 230VAC RD/GN — {projectName}</p>
      </div>

      {/* ═══ MAIN: Tor + Ampel + Steuerung — alles auf einem Screen ═══ */}
      <div className="px-3 pt-2 pb-2">
        {/* Row: Rolltor | Ampel | Hauptschalter */}
        <div className="flex gap-2 mb-2" data-testid="visuals-block">
          {/* Rolltor - compact */}
          <div className="flex-1 bg-white/[0.03] border border-white/8 rounded-xl p-2 flex flex-col items-center">
            <p className="text-[8px] uppercase tracking-[0.12em] text-gray-500 font-bold mb-1">Rolltor</p>
            <div className="relative w-full max-w-[80px] aspect-[4/5] border-2 border-gray-600 rounded overflow-hidden bg-gray-900/50">
              <div
                className="absolute left-0 right-0 top-0 bg-gradient-to-b from-gray-500 to-gray-600 transition-all duration-700"
                style={{ height: torOffen ? "12%" : "92%" }}
                data-testid="gate-visual"
              >
                {[...Array(6)].map((_, i) => <div key={i} className="border-b border-gray-700/50 h-[16%]" />)}
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-700" />
            </div>
            <p className={`text-[9px] font-bold mt-1 ${torOffen ? "text-amber-400" : "text-emerald-400"}`} data-testid="gate-status-text">
              {torAnimating ? "..." : torOffen ? "OFFEN" : "GESCHLOSSEN"}
            </p>
          </div>

          {/* Ampel - compact */}
          <div className="w-[72px] bg-white/[0.03] border border-white/8 rounded-xl p-2 flex flex-col items-center">
            <p className="text-[8px] uppercase tracking-[0.12em] text-gray-500 font-bold mb-1">Ampel</p>
            <div className="bg-gray-800 rounded-lg p-1.5 border border-gray-700 flex flex-col items-center gap-1.5" data-testid="ampel-visual">
              <div className={`w-9 h-9 rounded-full border-2 transition-all duration-300 ${
                ampelRot ? "bg-red-500 border-red-400 shadow-[0_0_20px_rgba(239,68,68,0.7)]" : "bg-gray-800 border-gray-600"
              }`} data-testid="ampel-rot" />
              <div className={`w-9 h-9 rounded-full border-2 transition-all duration-300 ${
                ampelGruen ? "bg-emerald-500 border-emerald-400 shadow-[0_0_20px_rgba(16,185,129,0.7)]" : "bg-gray-800 border-gray-600"
              }`} data-testid="ampel-gruen" />
            </div>
            <p className={`text-[9px] font-bold mt-1 ${ampelRot ? "text-red-400" : ampelGruen ? "text-emerald-400" : "text-gray-600"}`} data-testid="ampel-status-text">
              {ampelRot ? "ROT" : ampelGruen ? "GRUEN" : "AUS"}
            </p>
          </div>

          {/* Hauptschalter - rechts */}
          <div className="flex-1 flex flex-col gap-2">
            <button
              onClick={() => setHauptschalter(!hauptschalter)}
              className={`flex-1 rounded-xl text-xs font-bold uppercase tracking-wider flex flex-col items-center justify-center gap-1 transition-all active:scale-[0.97] border ${
                hauptschalter
                  ? "bg-emerald-600 text-white border-emerald-500"
                  : "bg-gray-800 text-gray-400 border-gray-700"
              }`}
              data-testid="hauptschalter-btn"
            >
              <Power className="w-5 h-5" />
              <span className="text-[10px]">{hauptschalter ? "EIN (I)" : "AUS (0)"}</span>
            </button>
          </div>
        </div>

        {/* Tor-Steuerung + Status */}
        <div className="flex gap-2 mb-2" data-testid="steuerung-block">
          <button
            onClick={() => animateGate(torOffen ? false : true)}
            disabled={!hauptschalter || torAnimating}
            className={`flex-1 h-11 rounded-lg text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all active:scale-[0.98] border ${
              !hauptschalter || torAnimating
                ? "bg-gray-800/50 text-gray-600 border-gray-800"
                : torOffen
                ? "bg-red-600/90 text-white border-red-500"
                : "bg-blue-600/90 text-white border-blue-500"
            }`}
            data-testid="tor-toggle-btn"
          >
            {torOffen ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            {torAnimating ? "..." : torOffen ? "Tor schliessen" : "Tor oeffnen"}
          </button>
          <div className="flex items-center gap-1.5 px-3 bg-white/[0.03] border border-white/8 rounded-lg min-w-[100px]">
            <div className={`w-2 h-2 rounded-full ${torOffen ? "bg-red-400" : "bg-emerald-400"}`} />
            <span className={`text-[10px] font-bold ${torOffen ? "text-red-400" : "text-emerald-400"}`}>
              {torOffen ? "Offen" : "Geschlossen"}
            </span>
          </div>
        </div>

        {/* Wahlschalter (3-Pos.) */}
        <div className="mb-2">
          <p className="text-[9px] uppercase tracking-[0.12em] text-gray-500 font-bold mb-1.5">Wahlschalter (3-Pos.)</p>
          <div className="grid grid-cols-3 gap-2">
            {[
              { pos: 1, label: "1", sub: "ROT" },
              { pos: 0, label: "0", sub: "AUS" },
              { pos: 2, label: "2", sub: "GRUEN" },
            ].map((item) => (
              <button
                key={item.pos}
                onClick={() => setWahlschalter(item.pos)}
                disabled={!hauptschalter}
                className={`h-12 rounded-lg flex flex-col items-center justify-center transition-all active:scale-[0.97] border ${
                  wahlschalter === item.pos && hauptschalter
                    ? "bg-white/10 border-white/30 text-white"
                    : hauptschalter
                    ? "bg-gray-800 border-gray-700 text-gray-400"
                    : "bg-gray-800/50 border-gray-800 text-gray-600"
                }`}
                data-testid={`wahlschalter-${item.pos}`}
              >
                <span className="text-[8px] uppercase tracking-wider text-gray-500 leading-none">POS</span>
                <span className="text-base font-bold leading-none">{item.label}</span>
              </button>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-2 mt-0.5">
            {[
              { sub: "ROT", color: "text-red-400/50" },
              { sub: "AUS", color: "text-gray-600" },
              { sub: "GRUEN", color: "text-emerald-400/50" },
            ].map((item, i) => (
              <p key={i} className={`text-[8px] font-bold text-center uppercase tracking-wider ${item.color}`}>{item.sub}</p>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ Details Toggle ═══ */}
      <div className="px-3 pb-3">
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="w-full py-2.5 rounded-lg text-[11px] font-semibold text-gray-400 bg-white/[0.04] border border-white/8 active:bg-white/[0.08] transition-colors"
          data-testid="details-toggle-btn"
        >
          {showDetails ? "Details ausblenden" : "Details anzeigen (Logik, UV, Schaltplan)"}
        </button>
      </div>

      {/* ═══ Detail Sections (collapsible) ═══ */}
      {showDetails && (
        <div className="px-3 pb-8 space-y-3">
          {/* SCHALTLOGIK */}
          <SchaltlogikBlock
            hauptschalter={hauptschalter}
            torOffen={torOffen}
            endschalterBetaetigt={endschalterBetaetigt}
            wahlschalter={wahlschalter}
            ampelRot={ampelRot}
            ampelGruen={ampelGruen}
          />

          {/* UNTERVERTEILUNG (UV) */}
          <UVBlock hauptschalter={hauptschalter} ampelRot={ampelRot} ampelGruen={ampelGruen} />

          {/* SCHALTBESCHREIBUNG */}
          <SchaltbeschreibungBlock ampelRot={ampelRot} ampelGruen={ampelGruen} wahlschalter={wahlschalter} torOffen={torOffen} />

          {/* BAUTEILLISTE */}
          <BauteillisteBlock />

          {/* KLEMMPLAN */}
          <KlemmplanBlock showFullscreen={showKlemmplanFullscreen} setShowFullscreen={setShowKlemmplanFullscreen} />

          {/* STROMLAUFPLAN (SVG) */}
          <SchaltplanBlock ampelRot={ampelRot} ampelGruen={ampelGruen} hauptschalter={hauptschalter}
            showFullscreen={showSchaltplanFullscreen} setShowFullscreen={setShowSchaltplanFullscreen} />
        </div>
      )}

      {/* Fullscreen Overlays */}
      {showSchaltplanFullscreen && (
        <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" data-testid="fullscreen-overlay" onClick={() => setShowSchaltplanFullscreen(false)}>
          <div className="flex items-center justify-between px-4 py-3 flex-shrink-0">
            <p className="text-sm font-semibold text-white">Stromlaufplan — Ampelschaltung Rolltor</p>
            <button onClick={() => setShowSchaltplanFullscreen(false)} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center active:bg-white/20" data-testid="fullscreen-close-btn">
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
          <div className="flex-1 overflow-auto flex items-center justify-center p-2" onClick={(e) => e.stopPropagation()} style={{ touchAction: "pinch-zoom" }}>
            <SchaltplanSVG ampelRot={ampelRot} ampelGruen={ampelGruen} hauptschalter={hauptschalter} className="w-full max-w-[700px]" />
          </div>
        </div>
      )}
      {showKlemmplanFullscreen && (
        <FullscreenImage title="Klemmplan — Verdrahtung Torampel" src={KLEMMPLAN_IMG} onClose={() => setShowKlemmplanFullscreen(false)} />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   SCHALTLOGIK
   ═══════════════════════════════════════════════════ */
function SchaltlogikBlock({ hauptschalter, torOffen, endschalterBetaetigt, wahlschalter, ampelRot, ampelGruen }) {
  const rows = [
    { label: "Hauptschalter:", value: hauptschalter ? "EIN" : "AUS", on: hauptschalter },
    { label: "Tor:", value: torOffen ? "OFFEN" : "GESCHLOSSEN", on: !torOffen, color: torOffen ? "text-red-400" : "text-emerald-400", dot: torOffen ? "bg-red-400" : "bg-emerald-400" },
    { label: "Endschalter:", value: endschalterBetaetigt ? "BETAETIGT" : "NICHT BETAETIGT", on: endschalterBetaetigt, color: endschalterBetaetigt ? "text-amber-400" : "text-gray-500", dot: endschalterBetaetigt ? "bg-amber-400" : undefined },
    { label: "→ Prioritaet:", value: endschalterBetaetigt && hauptschalter ? "ENDSCHALTER" : "WAHLSCHALTER", on: hauptschalter, color: endschalterBetaetigt && hauptschalter ? "text-amber-400" : "text-blue-400", dot: endschalterBetaetigt && hauptschalter ? "bg-amber-400" : "bg-blue-400" },
    { label: "Wahlschalter:", value: `POS ${wahlschalter}`, on: wahlschalter !== 0 && !endschalterBetaetigt, color: endschalterBetaetigt ? "text-gray-600" : undefined },
    { label: "→ Ampel ROT:", value: ampelRot ? "EIN" : "AUS", on: ampelRot, color: ampelRot ? "text-red-400" : undefined, dot: ampelRot ? "bg-red-400" : undefined },
    { label: "→ Ampel GRUEN:", value: ampelGruen ? "EIN" : "AUS", on: ampelGruen, color: ampelGruen ? "text-emerald-400" : undefined, dot: ampelGruen ? "bg-emerald-400" : undefined },
  ];

  return (
    <div className="bg-white/[0.03] border border-white/8 rounded-xl overflow-hidden" data-testid="schaltlogik-block">
      <div className="px-3 py-2 border-b border-white/5">
        <p className="text-[9px] uppercase tracking-[0.15em] text-gray-400 font-bold">Schaltlogik</p>
      </div>
      <div className="p-3 space-y-1.5">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${r.dot || (r.on ? "bg-emerald-400" : "bg-gray-600")}`} />
            <span className="text-xs text-gray-400">{r.label}</span>
            <span className={`text-xs font-bold ml-auto ${r.color || (r.on ? "text-emerald-400" : "text-gray-500")}`}>{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   UNTERVERTEILUNG (UV)
   ═══════════════════════════════════════════════════ */
function UVBlock({ hauptschalter, ampelRot, ampelGruen }) {
  return (
    <div className="bg-white/[0.03] border border-white/8 rounded-xl overflow-hidden" data-testid="uv-block">
      <div className="px-3 py-2 border-b border-white/5">
        <p className="text-[9px] uppercase tracking-[0.15em] text-gray-400 font-bold">Unterverteilung (UV)</p>
      </div>
      <div className="p-3">
        <p className="text-[8px] uppercase tracking-widest text-gray-600 font-bold mb-2">Klemmleiste — Zustandserfassung</p>
        <div className="space-y-1.5 mb-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">X1 Ampel ROT</span>
            <span className={`text-[10px] font-mono font-bold ${ampelRot ? "text-red-400" : "text-gray-600"}`}>{ampelRot ? "230V" : "0V"}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">X2 Ampel GRUEN</span>
            <span className={`text-[10px] font-mono font-bold ${ampelGruen ? "text-emerald-400" : "text-gray-600"}`}>{ampelGruen ? "230V" : "0V"}</span>
          </div>
        </div>
        <p className="text-[8px] uppercase tracking-widest text-gray-600 font-bold mb-2">Absicherung</p>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">Hauptschalter</span>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${hauptschalter ? "bg-emerald-500/15 text-emerald-400" : "bg-gray-700/50 text-gray-500"}`}>{hauptschalter ? "EIN" : "AUS"}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">LS-Schalter Ampel B16A</span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400">OK</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   SCHALTBESCHREIBUNG
   ═══════════════════════════════════════════════════ */
function SchaltbeschreibungBlock({ ampelRot, ampelGruen, wahlschalter, torOffen }) {
  const rules = [
    { cond: "Tor OFFEN (Endschalter)", result: "Immer ROT", active: ampelRot && torOffen, color: "red", priority: true },
    { cond: "Tor ZU + Pos 0", result: "Alles AUS", active: wahlschalter === 0 && !torOffen, color: "gray" },
    { cond: "Tor ZU + Pos 1", result: "ROT", active: ampelRot && !torOffen, color: "red" },
    { cond: "Tor ZU + Pos 2", result: "GRUEN", active: ampelGruen, color: "emerald" },
  ];
  const dotColor = { red: "bg-red-400", gray: "bg-gray-500", emerald: "bg-emerald-400" };
  const textColor = { red: "text-red-400", gray: "text-gray-400", emerald: "text-emerald-400" };

  return (
    <div className="bg-white/[0.03] border border-white/8 rounded-xl overflow-hidden" data-testid="schaltbeschreibung-block">
      <div className="px-3 py-2 border-b border-white/5">
        <p className="text-[9px] uppercase tracking-[0.15em] text-gray-400 font-bold">Schaltbeschreibung</p>
      </div>
      <div className="p-3 space-y-2">
        {rules.map((r, i) => (
          <div key={i} className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-colors ${r.active ? "bg-white/[0.04]" : ""}`}>
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${r.active ? dotColor[r.color] : "bg-gray-700"}`} />
            <span className="text-xs text-gray-400 flex-1">{r.cond}</span>
            <span className="text-[9px] text-gray-600 mx-1">→</span>
            <span className={`text-xs font-bold ${r.active ? textColor[r.color] : "text-gray-600"}`}>{r.result}</span>
            {r.priority && <span className="text-[7px] uppercase tracking-wider font-bold text-amber-400 bg-amber-400/10 px-1 py-0.5 rounded">Prio</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   BAUTEILLISTE
   ═══════════════════════════════════════════════════ */
function BauteillisteBlock() {
  const parts = [
    { id: "Q1", name: "Hauptschalter" },
    { id: "F1", name: "LS B16A" },
    { id: "S1", name: "M22-WRK3 (3-Pos)" },
    { id: "B1", name: "LS-11S/S Federstab" },
    { id: "H1", name: "WERMA ROT 230V" },
    { id: "H2", name: "WERMA GRUEN 230V" },
    { id: "X1/X2", name: "Erfassungsklemmen" },
    { id: "UV", name: "Unterverteilung" },
  ];

  return (
    <div className="bg-white/[0.03] border border-white/8 rounded-xl overflow-hidden" data-testid="bauteilliste-block">
      <div className="px-3 py-2 border-b border-white/5">
        <p className="text-[9px] uppercase tracking-[0.15em] text-gray-400 font-bold">Bauteilliste</p>
      </div>
      <div className="p-3 space-y-1.5">
        {parts.map((p, i) => (
          <div key={i} className="flex items-center gap-3">
            <span className="text-[10px] font-bold text-blue-400 w-8 text-right font-mono">{p.id}</span>
            <span className="text-[10px] text-gray-500">=</span>
            <span className="text-xs text-gray-300">{p.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   KLEMMPLAN
   ═══════════════════════════════════════════════════ */
function KlemmplanBlock({ showFullscreen, setShowFullscreen }) {
  return (
    <div className="bg-white/[0.03] border border-white/8 rounded-xl overflow-hidden" data-testid="klemmplan-block">
      <div className="px-3 py-2 border-b border-white/5 flex items-center justify-between">
        <p className="text-[9px] uppercase tracking-[0.15em] text-gray-400 font-bold">Klemmplan — Verdrahtung Torampel</p>
        <button onClick={() => setShowFullscreen(true)} className="text-gray-500 active:text-white p-1" data-testid="klemmplan-fullscreen-btn">
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Klemmen-Uebersicht */}
      <div className="px-3 pt-2 pb-1">
        <p className="text-[8px] uppercase tracking-widest text-gray-600 font-bold mb-1.5">Klemmenbelegung</p>
        <div className="space-y-1">
          <KlemmeRow klemme="X1" desc="Ausgang ROT (H1)" voltage="230V" color="text-red-400" />
          <KlemmeRow klemme="X2" desc="Ausgang GRUEN (H2)" voltage="230V" color="text-emerald-400" />
          <KlemmeRow klemme="X3" desc="N-Schiene (Neutralleiter)" voltage="N" color="text-blue-400" />
        </div>
      </div>

      {/* Verdrahtungshinweise */}
      <div className="px-3 pt-2 pb-2">
        <p className="text-[8px] uppercase tracking-widest text-gray-600 font-bold mb-1.5">Verdrahtung Endschalter B1</p>
        <div className="space-y-1">
          <div className="flex items-start gap-2">
            <span className="text-[9px] font-mono font-bold text-amber-400 w-12 flex-shrink-0">NO 13-14</span>
            <span className="text-[10px] text-gray-400">→ ROT (H1) — schaltet bei Tor OFFEN</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-[9px] font-mono font-bold text-amber-400 w-12 flex-shrink-0">NC 21-22</span>
            <span className="text-[10px] text-gray-400">→ GRUEN (H2) — unterbricht bei Tor OFFEN</span>
          </div>
        </div>
      </div>

      {/* Verdrahtungspfade */}
      <div className="px-3 pt-1 pb-2">
        <p className="text-[8px] uppercase tracking-widest text-gray-600 font-bold mb-1.5">Signalpfade</p>
        <div className="space-y-1.5">
          <PathRow path="L → Q1 → F1 → S1" desc="Versorgung" />
          <PathRow path="S1/1 → B1/NO → X1 → H1" desc="ROT-Pfad" color="text-red-400" />
          <PathRow path="S1/2 → B1/NC → X2 → H2" desc="GRUEN-Pfad" color="text-emerald-400" />
          <PathRow path="H1,H2 → X3 → N" desc="Rueckleiter" color="text-blue-400" />
        </div>
      </div>

      {/* Klemmplan Bild */}
      <button onClick={() => setShowFullscreen(true)} className="w-full p-3 pt-1 active:bg-white/[0.03] transition-colors">
        <img src={KLEMMPLAN_IMG} alt="Klemmplan Torampel" className="w-full rounded-lg border border-white/10" data-testid="klemmplan-img" />
      </button>
    </div>
  );
}

function KlemmeRow({ klemme, desc, voltage, color }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`text-[10px] font-mono font-bold ${color} w-6`}>{klemme}</span>
      <span className="text-[10px] text-gray-500">=</span>
      <span className="text-[10px] text-gray-400 flex-1">{desc}</span>
      <span className="text-[9px] font-mono text-gray-600">{voltage}</span>
    </div>
  );
}

function PathRow({ path, desc, color = "text-gray-400" }) {
  return (
    <div className="flex items-center gap-2 bg-white/[0.02] rounded px-2 py-1.5">
      <span className="text-[9px] font-mono text-gray-500 flex-1">{path}</span>
      <span className={`text-[9px] font-bold ${color}`}>{desc}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   STROMLAUFPLAN
   ═══════════════════════════════════════════════════ */
function SchaltplanBlock({ ampelRot, ampelGruen, hauptschalter, showFullscreen, setShowFullscreen }) {
  return (
    <div className="bg-white/[0.03] border border-white/8 rounded-xl overflow-hidden" data-testid="schaltplan-block">
      <div className="px-3 py-2 border-b border-white/5 flex items-center justify-between">
        <p className="text-[9px] uppercase tracking-[0.15em] text-gray-400 font-bold">Stromlaufplan — Ampelschaltung Rolltor</p>
        <button onClick={() => setShowFullscreen(true)} className="text-gray-500 active:text-white p-1" data-testid="schaltplan-fullscreen-btn">
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <button onClick={() => setShowFullscreen(true)} className="w-full p-3 active:bg-white/[0.03] transition-colors">
        <SchaltplanSVG ampelRot={ampelRot} ampelGruen={ampelGruen} hauptschalter={hauptschalter} className="w-full" />
      </button>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   FULLSCREEN IMAGE (wiederverwendbar)
   ═══════════════════════════════════════════════════ */
function FullscreenImage({ title, src, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" data-testid="fullscreen-overlay" onClick={onClose}>
      <div className="flex items-center justify-between px-4 py-3 flex-shrink-0">
        <p className="text-sm font-semibold text-white">{title}</p>
        <button onClick={onClose} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center active:bg-white/20" data-testid="fullscreen-close-btn">
          <X className="w-5 h-5 text-white" />
        </button>
      </div>
      <div className="flex-1 overflow-auto flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
        <img src={src} alt={title} className="max-w-full max-h-full object-contain" style={{ touchAction: "pinch-zoom" }} />
      </div>
    </div>
  );
}
